// Mercapoli - Contexto de Autenticación
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { auth as authApi } from '../lib/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe usarse dentro de AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Cargar usuario al iniciar
  useEffect(() => {
    const loadUser = async () => {
      const token = localStorage.getItem('mercapoli_token');
      const savedUser = localStorage.getItem('mercapoli_user');
      
      if (token && savedUser) {
        try {
          // Verificar que el token sigue siendo válido
          const userData = await authApi.getMe();
          setUser(userData);
          localStorage.setItem('mercapoli_user', JSON.stringify(userData));
        } catch (err) {
          // Token inválido, limpiar
          localStorage.removeItem('mercapoli_token');
          localStorage.removeItem('mercapoli_user');
          setUser(null);
        }
      }
      setLoading(false);
    };

    loadUser();
  }, []);

  const login = useCallback(async (email, password) => {
    setError(null);
    try {
      const response = await authApi.login({ email, password });
      localStorage.setItem('mercapoli_token', response.access_token);
      localStorage.setItem('mercapoli_user', JSON.stringify(response.user));
      setUser(response.user);
      return response.user;
    } catch (err) {
      const message = err.response?.data?.detail || 'Error al iniciar sesión';
      setError(message);
      throw new Error(message);
    }
  }, []);

  const register = useCallback(async (userData) => {
    setError(null);
    try {
      const response = await authApi.register(userData);
      localStorage.setItem('mercapoli_token', response.access_token);
      localStorage.setItem('mercapoli_user', JSON.stringify(response.user));
      setUser(response.user);
      return response.user;
    } catch (err) {
      const message = err.response?.data?.detail || 'Error al registrarse';
      setError(message);
      throw new Error(message);
    }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('mercapoli_token');
    localStorage.removeItem('mercapoli_user');
    setUser(null);
  }, []);

  const updateProfile = useCallback(async (data) => {
    try {
      await authApi.updateProfile(data);
      const updatedUser = { ...user, ...data };
      setUser(updatedUser);
      localStorage.setItem('mercapoli_user', JSON.stringify(updatedUser));
      return updatedUser;
    } catch (err) {
      const message = err.response?.data?.detail || 'Error al actualizar perfil';
      throw new Error(message);
    }
  }, [user]);

  const value = {
    user,
    loading,
    error,
    isAuthenticated: !!user,
    login,
    register,
    logout,
    updateProfile,
    clearError: () => setError(null),
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
